import { useQuery } from '@tanstack/react-query';

import { catalogueService } from '../../api/client';
import type {
  ETAReferenceCatalogue,
  ETAReferenceSelection,
  Quantity,
} from '../../gen/ejot/roofing/v1/types';
import { SectionCard, SelectField } from '../../shared/components';
import { EJOTProductSelect, type EJOTProductOption } from './EJOTProductSelect';
import { localEJOTProductImage } from './ejotProductImages';

interface ETAReferenceBrowserProps {
  selection?: ETAReferenceSelection;
  calculationActive?: boolean;
  onChange: (selection: ETAReferenceSelection | undefined) => void;
}

/**
 * Source-backed EJOT product picker for the ETA pull-out fallback. Product
 * identity and resistance rows remain exact source records. The frontend does
 * not infer conditional rows or engineering resistance; the backend applies
 * the component-only design conversion and keeps complete-system verification
 * unavailable.
 */
export function ETAReferenceBrowser({ selection, calculationActive = false, onChange }: ETAReferenceBrowserProps) {
  const query = useQuery({
    queryKey: ['eta-reference-catalogue'],
    queryFn: ({ signal }) => catalogueService.getETAReferenceCatalogue({}, signal),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const catalogue = query.data?.catalogue;
  const datasetId = catalogue?.dataset_id ?? selection?.dataset_id ?? '';
  const selectedProductId = selection?.product_id ?? '';
  const selectedComponentId = selection?.component_id ?? '';
  const selectedCombinationId = selection?.combination_id ?? '';
  const selectedSubstrateId = selection?.substrate_id ?? '';
  const selectedPullOutId = selection?.pull_out_id ?? '';

  const productsForSubstrate = sourceLinkedProductIds(catalogue, selectedSubstrateId);
  const productOptions: EJOTProductOption[] = (catalogue?.products ?? [])
    .filter((product) => selectedSubstrateId === '' || productsForSubstrate.has(product.product_id ?? ''))
    .map((product) => {
      const code = product.eta_product_code ?? product.product_id ?? '';
      return {
        value: product.product_id ?? '',
        name: code,
        meta: `ETA-07/0013${product.product_type ? ` · ${product.product_type}` : ''}`,
        imageSrc: localEJOTProductImage(code),
      };
    });

  const linkedCombinations = (catalogue?.combinations ?? []).filter((combination) => (
    selectedProductId === '' || combination.product_id === selectedProductId
  ));
  const linkedComponentIds = new Set(linkedCombinations.map((combination) => combination.component_id ?? ''));
  const componentOptions: EJOTProductOption[] = (catalogue?.components ?? [])
    .filter((component) => linkedComponentIds.has(component.component_id ?? ''))
    .map((component) => {
      const code = component.component_code ?? component.component_id ?? '';
      return {
        value: component.component_id ?? '',
        name: code,
        meta: `ETA-07/0013${component.component_type ? ` · ${component.component_type}` : ''}`,
        imageSrc: localEJOTProductImage(code),
      };
    });

  const combinationOptions = linkedCombinations
    .filter((combination) => selectedComponentId === '' || combination.component_id === selectedComponentId)
    .map((combination) => ({
      value: combination.combination_id ?? '',
      label: `ETA ${combination.eta_combination_code ?? combination.combination_id ?? ''}`,
    }));

  const substrateOptions = (catalogue?.substrates ?? []).map((substrate) => ({
    value: substrate.substrate_id ?? '',
    label: `${substrate.display_name ?? substrate.substrate_id ?? ''}${substrate.category ? ` — ${substrate.category}` : ''}`,
  }));

  const pullOut = (catalogue?.pull_out_resistances ?? []).filter((value) => (
    value.product_id === selectedProductId && value.substrate_id === selectedSubstrateId
  ));
  const pullOutOptions = pullOut.map((value) => ({
    value: value.pull_out_id ?? '',
    label: `${quantityLabel(value.characteristic_resistance)}${conditionLabel(value.condition_comparator, value.condition_value) ? ` · ${conditionLabel(value.condition_comparator, value.condition_value)}` : ''}`,
  }));

  const update = (patch: Partial<ETAReferenceSelection>) => onChange({
    dataset_id: datasetId,
    ...selection,
    ...patch,
  });

  if (query.isError) {
    return (
      <SectionCard title="EJOT products">
        <p className="field-note field-note-blocking">The validated EJOT ETA product source could not be loaded.</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="EJOT products" defaultOpen>
      <SelectField
        label="ETA substrate"
        value={selectedSubstrateId}
        options={substrateOptions}
        placeholder={query.isLoading ? 'Loading' : 'Select exact substrate'}
        onChange={(value) => update({
          substrate_id: value || undefined,
          product_id: undefined,
          component_id: undefined,
          combination_id: undefined,
          pull_out_id: undefined,
          pull_through_id: undefined,
        })}
      />

      <EJOTProductSelect
        label="Fastener"
        value={selectedProductId}
        options={productOptions}
        placeholder={selectedSubstrateId ? 'Select EJOT fastener' : 'Select a substrate first'}
        onChange={(value) => update({
          product_id: value || undefined,
          component_id: undefined,
          combination_id: undefined,
          pull_out_id: undefined,
          pull_through_id: undefined,
        })}
      />

      {componentOptions.length > 0 && (
        <EJOTProductSelect
          label="Fixing element"
          value={selectedComponentId}
          options={componentOptions}
          placeholder={selectedProductId ? 'Select EJOT fixing element' : 'Select a fastener first'}
          onChange={(value) => update({
            component_id: value || undefined,
            combination_id: undefined,
            pull_through_id: undefined,
          })}
        />
      )}

      {combinationOptions.length > 0 && (
        <SelectField
          label="ETA combination"
          value={selectedCombinationId}
          options={combinationOptions}
          placeholder="Select exact combination if required"
          onChange={(value) => update({ combination_id: value || undefined, pull_through_id: undefined })}
        />
      )}

      <SelectField
        label="ETA pull-out resistance"
        value={selectedPullOutId}
        options={pullOutOptions}
        placeholder={selectedProductId && selectedSubstrateId ? 'Select exact pull-out row' : 'Select substrate and fastener first'}
        help="The backend uses this exact ETA characteristic pull-out record and applies the source-backed component material-factor policy. No row is interpolated or inferred."
        onChange={(value) => update({ pull_out_id: value || undefined })}
      />

      <div className="catalogue-inline-status" role="status">
        <strong>ETA pull-out basis</strong>
        <span>Calculated component basis · complete-system verification unavailable</span>
      </div>

      {calculationActive && selectedPullOutId === '' && (
        <p className="field-note field-note-blocking" role="status">Select one exact ETA pull-out resistance row to calculate.</p>
      )}

      {selection && (
        <button type="button" className="header-button" onClick={() => onChange(undefined)}>Clear EJOT product selection</button>
      )}
    </SectionCard>
  );
}

function sourceLinkedProductIds(catalogue: ETAReferenceCatalogue | undefined, substrateId: string): Set<string> {
  if (!catalogue || substrateId === '') return new Set();
  return new Set((catalogue.pull_out_resistances ?? [])
    .filter((value) => value.substrate_id === substrateId)
    .map((value) => value.product_id ?? ''));
}

function quantityLabel(quantity: Quantity | undefined): string {
  if (!quantity) return 'No value';
  const precision = quantity.display_precision ?? 3;
  return `${(quantity.value ?? 0).toFixed(precision)} ${quantity.unit ?? ''}`.trim();
}

function conditionLabel(comparator: string | undefined, quantity: Quantity | undefined): string {
  if (!quantity) return '';
  return `${comparator ?? ''} ${quantityLabel(quantity)}`.trim();
}
